import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { selectTriggerVariants } from './select-variants';
import {
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  POSITION_SCALE,
  COLOR_SCALE,
  SHADOW_SCALE,
  Z_INDEX,
  SIZE_SCALE,
  TYPE_LEADING,
  MARGIN_X,
  MARGIN_Y,
} from './tokens.ts';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> {
  size?: 'sm' | 'md' | 'lg';
  radius?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, size, radius, fullWidth = true, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      selectTriggerVariants({
        size: size ?? 'md',
        radius: (radius === 'xl' ? 'lg' : radius) ?? 'md',
        fullWidth,
      }),
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className={`${SIZE_SCALE['4']} opacity-70`} aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      `flex cursor-default items-center justify-center ${PADDING_Y['2']} text-muted-foreground`,
      className
    )}
    {...props}
  >
    <ChevronUp className={`${SIZE_SCALE['4']}`} aria-hidden="true" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      `flex cursor-default items-center justify-center ${PADDING_Y['2']} text-muted-foreground`,
      className
    )}
    {...props}
  >
    <ChevronDown className={`${SIZE_SCALE['4']}`} aria-hidden="true" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

interface SelectContentProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Content
> {
  position?: 'item-aligned' | 'popper';
}

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal container={typeof document !== 'undefined' ? document.body : undefined}>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        `relative z-${Z_INDEX.dropdown} max-h-96 min-w-[12rem] overflow-hidden ${RADIUS_SCALE.xl} ${COLOR_SCALE['select-border']} bg-popover text-popover-foreground ${SHADOW_SCALE.xl} backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1.5 data-[side=left]:-translate-x-1.5 data-[side=right]:translate-x-1.5 data-[side=top]:-translate-y-1.5',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          SURFACE_PADDING['1'],
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        `${PADDING_X['3']} ${PADDING_Y['2']} ${TYPE_SCALE.xs} font-semibold uppercase tracking-wide text-muted-foreground`,
        className
      )}
      {...props}
    />
  )
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      `relative flex ${SIZE_SCALE.full} cursor-default select-none items-start ${RADIUS_SCALE.md} ${PADDING_X['3']} ${PADDING_Y['2']} pr-8 ${TYPE_SCALE.sm} outline-none transition-colors duration-150 ease-in-out focus:bg-primary/10 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-60`,
      className
    )}
    {...props}
  >
    <span
      className={`absolute ${POSITION_SCALE.right['2']} ${POSITION_SCALE.top['2']} flex ${SIZE_SCALE['4']} items-center justify-center flex-shrink-0`}
    >
      <SelectPrimitive.ItemIndicator>
        <Check className={`${SIZE_SCALE['4']} text-primary`} aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText className={`break-words ${TYPE_LEADING.snug}`}>
      {children}
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(
      `${MARGIN_X['1']} ${MARGIN_Y['1']} h-px ${COLOR_SCALE['select-separator']}`,
      className
    )}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
